<template>
  <div class="animated fadeIn col-md-12">
    <b-row>
      <b-col cols="12">
        <b-card>
          <div slot="header">
            {{ $lang.edit_user_info }}:
            <strong v-text="person_name" />
          </div>
          <b-overlay :show="isLoading">
            <b-form @submit.prevent="save">
              <b-form-group
                :label="$lang.mobile"
                label-for="mobile"
                :label-cols="12"
              >
                <b-form-input
                  id="mobile"
                  type="text"
                  :autocomplete="$lang.mobile"
                  v-model="$v.mobile.$model"
                ></b-form-input>
              </b-form-group>
              <b-form-group
                :label="$lang.email"
                label-for="email"
                :label-cols="12"
              >
                <requiredComponent
                  slot="label"
                  :label="$lang.email"
                  :rules="$v.email.$params"
                />
                <b-form-input
                  id="email"
                  type="text"
                  :autocomplete="$lang.email"
                  :state="$v.email.$dirty ? !$v.email.$error : null"
                  v-model="$v.email.$model"
                ></b-form-input>
                <b-form-invalid-feedback id="email-feedback">{{
                  $lang.error_required | replace($lang.email)
                }}</b-form-invalid-feedback>
              </b-form-group>
              <b-form-group
                :label="$lang.expire_at"
                label-for="expire_at"
                :label-cols="$config.form.labelCol"
              >
                <requiredComponent
                  slot="label"
                  :label="$lang.expire_at"
                  :rules="$v.expire_at.$params"
                  :showInfo="true"
                  :titleText="$lang.expire_at_tooltip"
                />
                <date-picker
                  :clearable="true"
                  v-model="$v.expire_at.$model"
                  :state="$v.expire_at.$dirty ? !$v.expire_at.$error : null"
                  format="YYYY/MM/DD"
                  display-format="dddd jDD jMMMM jYYYY"
                  min="2020/09/27"
                  inputReq="true"
                  :class="
                    $v.expire_at.$dirty
                      ? $v.expire_at.$error
                        ? 'invalid-date'
                        : 'valid-date'
                      : null
                  "
                >
                </date-picker>
                <b-form-invalid-feedback
                  :style="{ display: $v.expire_at.$error ? 'block' : 'none' }"
                  id="gender_id-feedback"
                >
                  {{
                    $lang.error_required | replace($lang.expire_at)
                  }}</b-form-invalid-feedback
                >
              </b-form-group>
              <b-form-group
                v-if="isActiveSmsPanel()"
                label-for="two_step_verification"
                :label-cols="12"
              >
                <b-form-checkbox
                  id="two_step_verification"
                  v-model="$v.two_step_verification.$model"
                  >{{ $lang.two_step_verification }}</b-form-checkbox
                >
              </b-form-group>

              <b-form-group
                v-if="isActiveSmsPanel()"
                label-for="login_notify"
                :label-cols="12"
              >
                <b-form-checkbox
                  id="login_notify"
                  v-model="$v.login_notify.$model"
                  >{{ $lang.login_notify }}</b-form-checkbox
                >
              </b-form-group>

              <div slot="footer" class="form-group form-btn">
                <b-button
                  type="submit"
                  v-if="showPermissionButton"
                  size="md"
                  variant="success"
                >
                  {{ $lang.submitButton }}
                  <i class="fa fa-check"></i>
                </b-button>

                <b-button
                  class="ml-2"
                  size="md"
                  variant="danger"
                  @click="handleBack"
                >
                  {{ $lang.close }}
                  <i class="fa fa-ban"></i>
                </b-button>
              </div>
            </b-form>
          </b-overlay>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { required, numeric } from "vuelidate/lib/validators";
import VuePersianDatetimePicker from "vue-persian-datetime-picker";
import { pathOr } from "ramda";
import { isActiveSmsPanel } from "@/shared/utils";

export default {
  name: "edit-user",
  components: {
    datePicker: VuePersianDatetimePicker,
  },
  validations: {
    mobile: {},
    email: {},
    two_step_verification: {},
    login_notify: {},
    expire_at: {},
  },
  data() {
    return {
      isLoading: false,
      id: this.$route.params.code,
      email: "",
      mobile: "",
      expire_at: "",
      permission: [],
      person_name: "",
      allowPermissions: ["admin", "edit"],
      showPermissionButton: false,
      login_notify: false,
      two_step_verification: false,
    };
  },
  methods: {
    isActiveSmsPanel,
    handleBack() {
      window.history.length > 1 ? this.$router.go(-1) : window.close();
    },
    save() {
      this.isLoading = true;
      this.$v.$touch();
      if (this.$v.$anyError) {
        return;
      }

      let payload = {
        mobile: this.mobile,
        email: this.email,
        two_step_verification: this.two_step_verification,
        login_notify: this.login_notify,
        expire_at: this.expire_at,
      };
      this.$httpService
        .put(
          this.$config.api_url + this.$config.resources.user + "/" + this.id,
          payload
        )
        .then((response) => {
          const data = pathOr([], ["data", "data"], response);
          this.email = pathOr("", ["email"], data);
          this.two_step_verification = pathOr(
            false,
            ["two_step_verification"],
            data
          );
          this.login_notify = pathOr(false, ["login_notify"], data);
          this.expire_at = pathOr(null, ["expire_at"], data);

          this.$notify.success(response.data.message);
          this.isLoading = false;
        })
        .catch((err) => {
          this.$notify.error(err.response.data.message);
          this.isLoading = false;
        });
    },
    fetchData() {
      return this.$httpService
        .get(this.$config.api_url + this.$config.resources.user + "/" + this.id)
        .then((response) => {
          let data = response.data.data;
          this.permission = response.data.permissions;
          this.email = data.email;
          this.mobile = data.mobile;
          this.expire_at = pathOr("", ["expire_at", "gregorian"], data);
          this.person_name = `${data.person.name} ${data.person.family} `;

          this.login_notify = data.login_notify;
          this.two_step_verification = data.two_step_verification;
          return true;
        })
        .catch((err) => {
          this.$notify.error(err.message);
          return false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    PermissionButtons() {
      this.permission.map((x) => {
        if (this.allowPermissions.includes(x)) {
          this.showPermissionButton = true;
        }
      });
    },
  },
  watch: {
    login_notify() {
      if (this.login_notify == true) this.two_step_verification = false;
    },
    two_step_verification() {
      if (this.two_step_verification == true) this.login_notify = false;
    },
  },
  created() {
    this.isLoading = true;
    this.fetchData().then(() => this.PermissionButtons());
  },
};
</script>

